import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { NumericFormat } from 'react-number-format';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import Select from 'react-select';
import { UserDashboardLayout } from '../../layouts/UserDashboardLayout';
import { getUserProfile, updateUserProfile } from '../../services/ProfileService';
import { getCountries, getCitiesByCountry } from '../../services/SelectorsService';
import { LANGUAGES, parseLanguagesString, formatLanguagesString } from '../../constants/languages';
import { LogoSpinner } from '../../components/LogoSpinner';
import swal from 'sweetalert';

// Schema de validación
const profileSchema = yup.object().shape({
    telefono: yup.string()
        .nullable()
        .matches(/^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/, 'Formato de teléfono inválido'),
    direccion: yup.string().nullable(),
    tituloProfesional: yup.string().nullable(),
    fechaNacimiento: yup.string()
        .nullable()
        .test('is-valid-date', 'Fecha inválida', function (value) {
            if (!value) return true;
            const date = new Date(value);
            if (isNaN(date.getTime())) return false;
            return date <= new Date();
        }),
    salarioActual: yup.number()
        .nullable()
        .min(0, 'El salario actual debe ser mayor o igual a 0')
        .typeError('Debe ser un número válido'),
    salarioDeseado: yup.number()
        .nullable()
        .min(0, 'El salario deseado debe ser mayor o igual a 0')
        .typeError('Debe ser un número válido'),
    idiomas: yup.string().nullable(),
    genero: yup.string().nullable().oneOf(['Masculino', 'Femenino', null], 'Género inválido'),
    telefonoContacto: yup.string()
        .nullable()
        .matches(/^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/, 'Formato de teléfono inválido'),
    paisResidencia: yup.string().nullable(),
    ciudadResidencia: yup.string().nullable(),
    linkedin: yup.string()
        .nullable()
        .url('Debe ser una URL válida'),
    facebook: yup.string()
        .nullable()
        .url('Debe ser una URL válida'),
    instagram: yup.string()
        .nullable()
        .url('Debe ser una URL válida'),
    github: yup.string()
        .nullable()
        .url('Debe ser una URL válida'),
    x: yup.string()
        .nullable()
        .url('Debe ser una URL válida'),
});

export function Profile() {
    const queryClient = useQueryClient();
    const [selectedCountryId, setSelectedCountryId] = useState(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    // Preparar opciones de idiomas para el Select
    const languageOptions = LANGUAGES.map(lang => ({ label: lang, value: lang }));

    const {
        register,
        handleSubmit,
        control,
        formState: { errors, isSubmitting },
        reset,
        watch,
        setValue,
    } = useForm({
        resolver: yupResolver(profileSchema),
        defaultValues: {
            telefono: '',
            direccion: '',
            tituloProfesional: '',
            fechaNacimiento: '',
            salarioActual: null,
            salarioDeseado: null,
            idiomas: '',
            genero: null,
            telefonoContacto: '',
            paisResidencia: '',
            ciudadResidencia: '',
            linkedin: '',
            facebook: '',
            instagram: '',
            github: '',
            x: '',
        },
    });

    const watchedPaisResidencia = watch('paisResidencia');

    // Query para obtener el perfil del usuario
    const { data: profileData, isLoading: isLoadingProfile, error: profileError } = useQuery(
        'userProfile',
        async () => {
            const response = await getUserProfile();
            return response.data;
        },
        {
            refetchOnMount: true, // Forzar refetch al montar el componente
        }
    );

    // useEffect para actualizar el formulario cuando profileData cambie (incluyendo datos del caché)
    useEffect(() => {
        if (profileData && !isLoadingProfile) {
            // Mapear datos del API al formulario
            let fechaNacimiento = null;
            if (profileData.fechaNacimiento) {
                // Convertir fecha ISO a formato YYYY-MM-DD para el input date
                const date = new Date(profileData.fechaNacimiento);
                if (!isNaN(date.getTime())) {
                    fechaNacimiento = date.toISOString().split('T')[0];
                }
            }

            // Mapear país y ciudad: el API puede devolver IDs, objetos o nombres
            let paisResidenciaId = null;
            let ciudadResidenciaId = null;

            // Manejar paisResidencia - puede venir como paisResidenciaId, paisResidencia (objeto o string)
            if (profileData.paisResidenciaId) {
                paisResidenciaId = profileData.paisResidenciaId;
            } else if (profileData.paisResidencia) {
                // Si el país viene como objeto, usar el ID
                if (typeof profileData.paisResidencia === 'object' && profileData.paisResidencia.id) {
                    paisResidenciaId = profileData.paisResidencia.id;
                } else if (typeof profileData.paisResidencia === 'string') {
                    // Si viene como string, puede ser un nombre o un ID
                    paisResidenciaId = profileData.paisResidencia;
                }
            }

            // Manejar ciudadResidencia - puede venir como ciudadResidenciaId, ciudadResidencia (objeto o string)
            if (profileData.ciudadResidenciaId) {
                ciudadResidenciaId = profileData.ciudadResidenciaId;
            } else if (profileData.ciudadResidencia) {
                if (typeof profileData.ciudadResidencia === 'object' && profileData.ciudadResidencia.id) {
                    ciudadResidenciaId = profileData.ciudadResidencia.id;
                } else if (typeof profileData.ciudadResidencia === 'string') {
                    ciudadResidenciaId = profileData.ciudadResidencia;
                }
            }

            // Establecer país seleccionado primero para cargar ciudades
            if (paisResidenciaId) {
                // Si parece un UUID, usarlo directamente; si no, será procesado cuando se carguen los países
                if (paisResidenciaId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                    setSelectedCountryId(paisResidenciaId);
                }
            }

            reset({
                telefono: profileData.telefono || '',
                direccion: profileData.direccion || '',
                tituloProfesional: profileData.tituloProfesional || '',
                fechaNacimiento: fechaNacimiento || '',
                salarioActual: profileData.salarioActual || null,
                salarioDeseado: profileData.salarioDeseado || null,
                idiomas: profileData.idiomas || '',
                genero: profileData.genero || null,
                telefonoContacto: profileData.telefonocontacto || profileData.telefonoContacto || '',
                paisResidencia: paisResidenciaId || '',
                ciudadResidencia: ciudadResidenciaId || '',
                linkedin: profileData.linkedin || '',
                facebook: profileData.facebook || '',
                instagram: profileData.instagram || '',
                github: profileData.github || '',
                x: profileData.x || '',
            });

            // Marcar que la carga inicial está completa
            setIsInitialLoad(false);
        }
    }, [profileData, isLoadingProfile, reset]);

    // Query para obtener países
    const { data: countriesData, isLoading: isLoadingCountries } = useQuery(
        'countries',
        async () => {
            const response = await getCountries();
            return response.data;
        },
        {
            onSuccess: (countries) => {
                // Si el país en el formulario es un nombre, buscar el ID
                const currentPaisResidencia = watch('paisResidencia');
                if (currentPaisResidencia && countries && !currentPaisResidencia.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                    const country = countries.find(c => c.nombre === currentPaisResidencia);
                    if (country) {
                        setValue('paisResidencia', country.id);
                        setSelectedCountryId(country.id);
                    }
                }
            },
        }
    );

    // Query para obtener ciudades según el país seleccionado
    const { data: citiesData, isLoading: isLoadingCities } = useQuery(
        ['cities', selectedCountryId],
        async () => {
            if (!selectedCountryId) return [];
            const response = await getCitiesByCountry(selectedCountryId);
            return response.data;
        },
        {
            enabled: !!selectedCountryId,
        }
    );

    // Efecto para establecer la ciudad cuando las ciudades se cargan y hay un valor guardado
    useEffect(() => {
        if (citiesData && citiesData.length > 0 && selectedCountryId) {
            const currentCiudadId = watch('ciudadResidencia');
            if (currentCiudadId) {
                // Verificar que la ciudad existe en la lista cargada
                const ciudadExists = citiesData.some(city => city.id === currentCiudadId);
                if (ciudadExists) {
                    // Asegurarse de que el valor se mantenga en el formulario
                    setValue('ciudadResidencia', currentCiudadId, { shouldValidate: false });
                }
            }
        }
    }, [citiesData, selectedCountryId, watch, setValue]);

    // Mutation para actualizar el perfil
    const updateProfileMutation = useMutation(
        async (data) => {
            // Preparar datos para el API - ahora se envían paisResidenciaId y ciudadResidenciaId
            const apiData = {
                telefono: data.telefono || null,
                direccion: data.direccion || null,
                tituloProfesional: data.tituloProfesional || null,
                fechaNacimiento: data.fechaNacimiento || null,
                salarioActual: data.salarioActual ? parseInt(data.salarioActual) : null,
                salarioDeseado: data.salarioDeseado ? parseInt(data.salarioDeseado) : null,
                idiomas: data.idiomas || null,
                genero: data.genero || null,
                telefonoContacto: data.telefonoContacto || null,
                paisResidenciaId: data.paisResidencia || null,
                ciudadResidenciaId: data.ciudadResidencia || null,
                linkedin: data.linkedin || null,
                facebook: data.facebook || null,
                instagram: data.instagram || null,
                github: data.github || null,
                x: data.x || null,
            };

            return await updateUserProfile(apiData);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('userProfile');
                swal('Éxito', 'Perfil actualizado correctamente', 'success');
            },
            onError: (error) => {
                const errorMessage = error.response?.data?.message || error.message || 'Error al actualizar el perfil';
                swal('Error', errorMessage, 'error');
            },
        }
    );

    // Efecto para resetear ciudad cuando cambia el país (solo cuando el usuario cambia el país manualmente)
    useEffect(() => {
        // No ejecutar durante la carga inicial
        if (isInitialLoad) return;

        // Solo ejecutar si el país cambió y no es la carga inicial
        if (watchedPaisResidencia && watchedPaisResidencia !== selectedCountryId) {
            setSelectedCountryId(watchedPaisResidencia);
            // Resetear ciudad cuando el país cambia manualmente
            setValue('ciudadResidencia', '');
        } else if (!watchedPaisResidencia) {
            setSelectedCountryId(null);
            setValue('ciudadResidencia', '');
        }
    }, [watchedPaisResidencia, setValue, selectedCountryId, isInitialLoad]);

    const onSubmit = (data) => {
        updateProfileMutation.mutate(data);
    };


    const isLoading = isLoadingProfile || isLoadingCountries;
    const hasError = profileError;

    if (isLoading) {
        return <LogoSpinner />;
        
    }

    if (hasError) {
        return (
            <UserDashboardLayout>
                <div className="job-bx job-profile">
                    <div className="alert alert-danger" role="alert">
                        <h4 className="alert-heading">Error al cargar el perfil</h4>
                        <p>{profileError?.response?.data?.message || profileError?.message || 'Ha ocurrido un error al cargar el perfil. Por favor, intenta de nuevo.'}</p>
                    </div>
                </div>
            </UserDashboardLayout>
        );
    }

    return (
        <UserDashboardLayout>
            <div className="job-bx job-profile">
                <div className="job-bx-title clearfix">
                    <h5 className="font-weight-700 pull-left text-uppercase">Información Básica</h5>
                    <Link to={"./"} className="site-button right-arrow button-sm float-right">Atrás</Link>
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row m-b30">
                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>Título Profesional:</label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.tituloProfesional ? 'is-invalid' : ''}`}
                                    placeholder="Ej: Desarrollador Web"
                                    {...register('tituloProfesional')}
                                />
                                {errors.tituloProfesional && (
                                    <div className="invalid-feedback">{errors.tituloProfesional.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>Fecha de nacimiento:</label>
                                <input
                                    type="date"
                                    className={`form-control ${errors.fechaNacimiento ? 'is-invalid' : ''}`}
                                    {...register('fechaNacimiento')}
                                />
                                {errors.fechaNacimiento && (
                                    <div className="invalid-feedback">{errors.fechaNacimiento.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>Salario Actual:</label>
                                <Controller
                                    name="salarioActual"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <NumericFormat
                                            className={`form-control ${errors.salarioActual ? 'is-invalid' : ''}`}
                                            prefix='RD$'
                                            thousandSeparator
                                            value={value || ''}
                                            onValueChange={(values) => {
                                                onChange(values.floatValue || null);
                                            }}
                                        />
                                    )}
                                />
                                {errors.salarioActual && (
                                    <div className="invalid-feedback">{errors.salarioActual.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>Salario Deseado:</label>
                                <Controller
                                    name="salarioDeseado"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <NumericFormat
                                            className={`form-control ${errors.salarioDeseado ? 'is-invalid' : ''}`}
                                            prefix='RD$'
                                            thousandSeparator
                                            value={value || ''}
                                            onValueChange={(values) => {
                                                onChange(values.floatValue || null);
                                            }}
                                        />
                                    )}
                                />
                                {errors.salarioDeseado && (
                                    <div className="invalid-feedback">{errors.salarioDeseado.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>Idiomas:</label>
                                <Controller
                                    name="idiomas"
                                    control={control}
                                    render={({ field }) => {
                                        // Convertir el string de idiomas a formato para react-select
                                        const currentValue = field.value
                                            ? parseLanguagesString(field.value).map(lang =>
                                                ({ label: lang, value: lang })
                                            )
                                            : [];

                                        return (
                                            <Select
                                                {...field}
                                                value={currentValue}
                                                onChange={(selected) => {
                                                    const values = selected ? selected.map(opt => opt.value) : [];
                                                    field.onChange(formatLanguagesString(values));
                                                }}
                                                options={languageOptions}
                                                isMulti
                                                placeholder="Selecciona idiomas..."
                                                className="basic-multi-select"
                                                classNamePrefix="select"
                                                isSearchable
                                                noOptionsMessage={() => "No se encontraron idiomas"}
                                            />
                                        );
                                    }}
                                />
                                {errors.idiomas && (
                                    <div className="invalid-feedback d-block">{errors.idiomas.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>Género</label>
                                <div className="row">
                                    <div className="col-lg-6 col-md-6 col-sm-6 col-6">
                                        <div className="custom-control custom-radio">
                                            <input
                                                type="radio"
                                                className="custom-control-input"
                                                id="male"
                                                value="Masculino"
                                                {...register('genero')}
                                            />
                                            <label className="custom-control-label" htmlFor="male">Masculino</label>
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-6 col-6">
                                        <div className="custom-control custom-radio">
                                            <input
                                                type="radio"
                                                className="custom-control-input"
                                                id="female"
                                                value="Femenino"
                                                {...register('genero')}
                                            />
                                            <label className="custom-control-label" htmlFor="female">Femenino</label>
                                        </div>
                                    </div>
                                </div>
                                {errors.genero && (
                                    <div className="text-danger small">{errors.genero.message}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="job-bx-title clearfix">
                        <h5 className="font-weight-700 pull-left text-uppercase">Información de Contacto</h5>
                    </div>
                    <div className="row">
                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>Teléfono:</label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.telefono ? 'is-invalid' : ''}`}
                                    placeholder="+1 123 456 7890"
                                    {...register('telefono')}
                                />
                                {errors.telefono && (
                                    <div className="invalid-feedback">{errors.telefono.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>Teléfono de Contacto:</label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.telefonoContacto ? 'is-invalid' : ''}`}
                                    placeholder="+1 123 456 7890"
                                    {...register('telefonoContacto')}
                                />
                                {errors.telefonoContacto && (
                                    <div className="invalid-feedback">{errors.telefonoContacto.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>Dirección:</label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.direccion ? 'is-invalid' : ''}`}
                                    placeholder="Calle Principal 123"
                                    {...register('direccion')}
                                />
                                {errors.direccion && (
                                    <div className="invalid-feedback">{errors.direccion.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>País de Residencia</label>
                                <Form.Control
                                    as="select"
                                    custom
                                    className={`custom-select ${errors.paisResidencia ? 'is-invalid' : ''}`}
                                    {...register('paisResidencia')}
                                    disabled={isLoadingCountries}
                                >
                                    <option value="">Seleccione un país</option>
                                    {countriesData?.map((country) => (
                                        <option key={country.id} value={country.id}>
                                            {country.nombre}
                                        </option>
                                    ))}
                                </Form.Control>
                                {errors.paisResidencia && (
                                    <div className="invalid-feedback">{errors.paisResidencia.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>Ciudad de Residencia</label>
                                <Form.Control
                                    as="select"
                                    custom
                                    className={`custom-select ${errors.ciudadResidencia ? 'is-invalid' : ''}`}
                                    {...register('ciudadResidencia')}
                                    disabled={!selectedCountryId || isLoadingCities}
                                >
                                    <option value="">Seleccione una ciudad</option>
                                    {citiesData?.map((city) => (
                                        <option key={city.id} value={city.id}>
                                            {city.nombre}
                                        </option>
                                    ))}
                                </Form.Control>
                                {errors.ciudadResidencia && (
                                    <div className="invalid-feedback">{errors.ciudadResidencia.message}</div>
                                )}
                                {!selectedCountryId && (
                                    <small className="form-text text-muted">Primero seleccione un país</small>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="job-bx-title clearfix">
                        <h5 className="font-weight-700 pull-left text-uppercase">Redes Sociales</h5>
                    </div>
                    <div className="row">
                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>LinkedIn:</label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.linkedin ? 'is-invalid' : ''}`}
                                    placeholder="https://linkedin.com/in/usuario"
                                    {...register('linkedin')}
                                />
                                {errors.linkedin && (
                                    <div className="invalid-feedback">{errors.linkedin.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>Facebook</label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.facebook ? 'is-invalid' : ''}`}
                                    placeholder="https://facebook.com/usuario"
                                    {...register('facebook')}
                                />
                                {errors.facebook && (
                                    <div className="invalid-feedback">{errors.facebook.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>Instagram</label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.instagram ? 'is-invalid' : ''}`}
                                    placeholder="https://instagram.com/usuario"
                                    {...register('instagram')}
                                />
                                {errors.instagram && (
                                    <div className="invalid-feedback">{errors.instagram.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>GitHub</label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.github ? 'is-invalid' : ''}`}
                                    placeholder="https://github.com/usuario"
                                    {...register('github')}
                                />
                                {errors.github && (
                                    <div className="invalid-feedback">{errors.github.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label>X (Twitter)</label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.x ? 'is-invalid' : ''}`}
                                    placeholder="https://x.com/usuario"
                                    {...register('x')}
                                />
                                {errors.x && (
                                    <div className="invalid-feedback">{errors.x.message}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {updateProfileMutation.isError && (
                        <div className="alert alert-danger" role="alert">
                            Error al guardar el perfil. Por favor, intenta de nuevo.
                        </div>
                    )}

                    <button
                        type="submit"
                        className="site-button m-b30"
                        disabled={isSubmitting || updateProfileMutation.isLoading}
                    >
                        {isSubmitting || updateProfileMutation.isLoading ? 'Guardando...' : 'Guardar'}
                    </button>
                </form>
            </div>
        </UserDashboardLayout>
    );
}

